import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { COMPANY, LEGAL_LAST_UPDATED, LEGAL_PAGES } from '../lib/company';
import { useTranslation } from '../hooks/useTranslation';

export const LegalTable = ({ head, rows }) => (
  <div className="table-wrap">
    <table>
      <thead>
        <tr>{head.map((h) => <th key={h}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CompanyBlock = () => (
  <address className="not-italic border border-slate-800 bg-slate-900/60 p-5 font-mono text-[13px] leading-relaxed text-slate-300">
    <span className="block text-slate-50 font-semibold">{COMPANY.legalName}</span>
    trading as {COMPANY.tradingName}
    <br />
    Registered in {COMPANY.jurisdiction}, company number {COMPANY.companyNumber}
    <br />
    Registered office: {COMPANY.address}
    <br />
    Email: <a href={`mailto:${COMPANY.emails.legal}`}>{COMPANY.emails.legal}</a>
  </address>
);

/**
 * sections: [{ id, title, body }] where body is JSX. Rendered as numbered
 * headings with a sticky table of contents on wide screens.
 */
const LegalLayout = ({ title, intro, sections }) => {
  const { language } = useTranslation();
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-slate-800">
          <div className="absolute inset-0 bg-grid bg-grid-fade" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-400 mb-4">Legal · {COMPANY.legalName}</p>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-slate-50 mb-5">{title}</h1>
            <p className="font-mono text-xs text-slate-500">Last updated: {LEGAL_LAST_UPDATED}</p>
            {language === 'zh' && (
              <p className="mt-6 max-w-2xl border-l-2 border-cyan-500 pl-4 text-sm text-slate-300">
                本法律文件以英文版本为准。如需帮助，请联系 {COMPANY.emails.support}。
              </p>
            )}
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24 space-y-8">
              <nav aria-label="On this page" className="hidden lg:block">
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-3">On this page</p>
                <ol className="space-y-1.5 text-sm">
                  {sections.map((s, i) => (
                    <li key={s.id}>
                      <a href={`#${s.id}`} className="flex gap-3 text-slate-400 hover:text-cyan-400 transition-colors">
                        <span className="font-mono text-slate-600 w-5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                        <span>{s.title}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
              <nav aria-label="Legal documents">
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-3">Legal documents</p>
                <ul className="border-l border-slate-800 text-sm">
                  {LEGAL_PAGES.map((p) => (
                    <li key={p.href}>
                      <Link
                        to={p.href}
                        className={`block -ml-px border-l-2 pl-3 py-1.5 transition-colors ${pathname === p.href ? 'border-cyan-500 text-slate-50' : 'border-transparent text-slate-400 hover:text-slate-100'}`}
                      >
                        {p.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </aside>

          <article className="lg:col-span-9 order-1 lg:order-2 legal-prose max-w-3xl">
            {intro}
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <h2>
                  <span className="font-mono text-base text-cyan-500 mr-3 align-middle">{String(i + 1).padStart(2, '0')}</span>
                  {s.title}
                </h2>
                {s.body}
              </section>
            ))}
            <div className="mt-14">
              <CompanyBlock />
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalLayout;
